var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a, _b;
import { Vue, Component, Prop, Watch } from "vue-facing-decorator";
import Multiselect from "@/app/vues/components/multiselect";
import { SystemsClient, PreviewControllerImportCommand, PreviewControllerImportRequestModel, EnqueueControllerImportCommand, EnqueueControllerImportRequestModel, EnqueueControllerImportFileModel } from "@/app/api/battery-portal-api";
import { Notifier } from "@/common/utils/notifier";
import { UploadErrorHelper } from "@/app/utils/upload-error-helper";
const systemsClient = new SystemsClient(window.apiUrl);
/** Same limit as UploadProfiles.ControllerImportArchiveMaxBytes on the server. */
const MaxFileBytes = 500 * 1024 * 1024;
/**
 * Import Controller Data: many ZIP files for one company and site. Each file is checked on the
 * server first (which also keeps it there); the user then decides about any existing systems and
 * queues the lot. The import itself runs in the background and reports in the Background tasks panel.
 */
let ImportSystemFromZipModal = class ImportSystemFromZipModal extends Vue {
    constructor() {
        super(...arguments);
        this.show = false;
        this.selectedCompany = null;
        this.selectedSite = null;
        this.rows = [];
        this.checking = false;
        this.cancelRequested = false;
        this.checkProgress = { current: 0, total: 0 };
        this.queueing = false;
        this.enqueueErrors = [];
        this.nextKey = 0;
    }
    showModal() {
        this.resetForm();
        this.show = true;
    }
    resetForm() {
        this.selectedCompany = null;
        this.selectedSite = null;
        this.rows = [];
        this.checking = false;
        this.cancelRequested = false;
        this.queueing = false;
        this.enqueueErrors = [];
        this.clearFileInput();
    }
    onModalHidden() {
        // Stops a check in progress; files already checked are cleaned up by the server later.
        this.cancelRequested = true;
        this.resetForm();
    }
    cancel() {
        if (!this.queueing)
            this.show = false;
    }
    // --- Selection ---
    onCompanyChanged() {
        this.selectedSite = null;
        this.resetChecks();
    }
    onSiteChanged() {
        this.resetChecks();
    }
    /** A check is only valid for the company and site it was made for. */
    resetChecks() {
        this.enqueueErrors = [];
        this.rows.forEach(row => this.resetRow(row));
    }
    onFilesSelected(event) {
        var _a;
        const input = event.target;
        const files = Array.from((_a = input.files) !== null && _a !== void 0 ? _a : []);
        this.enqueueErrors = [];
        for (const file of files) {
            // The same file picked twice is listed once.
            if (this.rows.some(r => r.file.name === file.name && r.file.size === file.size && r.file.lastModified === file.lastModified))
                continue;
            const row = {
                key: `f${this.nextKey++}`,
                file,
                state: "Selected",
                preview: null,
                message: "",
                errors: [],
                errorReference: null,
                choice: null,
                archiveConfirmed: false
            };
            const problem = this.clientSideProblem(file);
            if (problem) {
                row.state = "Rejected";
                row.message = problem;
            }
            this.rows.push(row);
        }
        // Lets the user pick more files, including ones with the same name.
        this.clearFileInput();
    }
    clientSideProblem(file) {
        if (!file.name.toLowerCase().endsWith(".zip"))
            return "This is not a ZIP file.";
        if (file.size === 0)
            return "The file is empty.";
        if (file.size > MaxFileBytes)
            return `The file is larger than ${this.formatSize(MaxFileBytes)}.`;
        return null;
    }
    removeRow(row) {
        this.rows = this.rows.filter(r => r !== row);
        this.enqueueErrors = [];
    }
    clearFiles() {
        this.rows = [];
        this.enqueueErrors = [];
    }
    clearFileInput() {
        const input = this.$refs.fileInput;
        if (input)
            input.value = "";
    }
    resetRow(row) {
        if (row.state === "Rejected" && !row.preview)
            return; // rejected in the browser: still true for any site
        row.state = "Selected";
        row.preview = null;
        row.message = "";
        row.errors = [];
        row.errorReference = null;
        row.choice = null;
        row.archiveConfirmed = false;
    }
    // --- Checking ---
    checkFiles() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (!this.canCheck)
                return;
            const toCheck = this.rows.filter(r => r.state === "Selected");
            const companyId = this.selectedCompany.value;
            const siteId = this.selectedSite.value;
            this.checking = true;
            this.cancelRequested = false;
            this.enqueueErrors = [];
            this.checkProgress = { current: 0, total: toCheck.length };
            try {
                // One at a time: each file is read into memory and uploaded on its own.
                for (const row of toCheck) {
                    if (this.cancelRequested || !this.rows.includes(row))
                        continue;
                    this.checkProgress.current++;
                    row.state = "Checking";
                    yield this.checkRow(row, companyId, siteId);
                    // The location changed while this file was being checked: the result is not for it.
                    if (((_a = this.selectedCompany) === null || _a === void 0 ? void 0 : _a.value) !== companyId || ((_b = this.selectedSite) === null || _b === void 0 ? void 0 : _b.value) !== siteId)
                        this.resetRow(row);
                }
            }
            finally {
                this.rows.filter(r => r.state === "Checking").forEach(r => this.resetRow(r));
                this.checking = false;
                this.cancelRequested = false;
            }
        });
    }
    checkRow(row, companyId, siteId) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            try {
                const base64 = yield this.readFileAsBase64(row.file);
                const response = yield systemsClient.previewImportFromZip(new PreviewControllerImportCommand({
                    requestModel: new PreviewControllerImportRequestModel({
                        zipFileBase64: base64,
                        fileName: row.file.name,
                        companyId,
                        siteId
                    })
                }));
                row.preview = response;
                row.errors = (_a = response.errors) !== null && _a !== void 0 ? _a : [];
                row.errorReference = (_b = response.errorReference) !== null && _b !== void 0 ? _b : null;
                row.message = (_c = response.message) !== null && _c !== void 0 ? _c : "";
                if (!response.success || !response.stagingId) {
                    row.state = "Rejected";
                    if (!row.message)
                        row.message = "This file cannot be imported.";
                }
                else if (response.existingSystemFound) {
                    row.state = "NeedsChoice";
                }
                else {
                    row.state = "Ready";
                }
            }
            catch (ex) {
                row.state = "Rejected";
                row.preview = null;
                row.errors = [];
                row.message = UploadErrorHelper.getMessage(ex) || "The file could not be checked.";
            }
        });
    }
    readFileAsBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                // Without the data URL prefix (e.g. "data:application/zip;base64,")
                resolve(reader.result.split(",")[1]);
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }
    // --- Decisions ---
    isDifferentLocation(row) {
        var _a;
        return !!((_a = row.preview) === null || _a === void 0 ? void 0 : _a.existingSystemInDifferentLocation);
    }
    get sameSiteChoiceRows() {
        return this.rows.filter(r => r.state === "NeedsChoice" && !this.isDifferentLocation(r));
    }
    get differentSiteChoiceRows() {
        return this.rows.filter(r => r.state === "NeedsChoice" && this.isDifferentLocation(r));
    }
    chooseForAll(choice) {
        this.sameSiteChoiceRows.forEach(r => r.choice = choice);
    }
    confirmArchiveForAll() {
        this.differentSiteChoiceRows.forEach(r => r.archiveConfirmed = true);
    }
    isDecided(row) {
        if (row.state !== "NeedsChoice")
            return true;
        return this.isDifferentLocation(row) ? row.archiveConfirmed : row.choice !== null;
    }
    /** Checked files that are not rejected, keyed by controller serial number (case-insensitive). */
    get rowsBySerial() {
        var _a, _b, _c;
        const map = new Map();
        for (const row of this.rows) {
            if (row.state !== "Ready" && row.state !== "NeedsChoice")
                continue;
            const serial = ((_b = (_a = row.preview) === null || _a === void 0 ? void 0 : _a.serialNumber) !== null && _b !== void 0 ? _b : "").trim().toLowerCase();
            if (!serial)
                continue;
            map.set(serial, [...((_c = map.get(serial)) !== null && _c !== void 0 ? _c : []), row]);
        }
        return map;
    }
    /** The other files in this batch for the same controller. */
    duplicateOf(row) {
        var _a, _b, _c;
        const serial = ((_b = (_a = row.preview) === null || _a === void 0 ? void 0 : _a.serialNumber) !== null && _b !== void 0 ? _b : "").trim().toLowerCase();
        const group = serial ? (_c = this.rowsBySerial.get(serial)) !== null && _c !== void 0 ? _c : [] : [];
        return group.length > 1 ? group.filter(r => r !== row).map(r => r.file.name) : [];
    }
    get importableRows() {
        return this.rows.filter(r => r.state === "Ready" || r.state === "NeedsChoice");
    }
    get counts() {
        return {
            ready: this.rows.filter(r => r.state === "Ready" && !this.duplicateOf(r).length).length,
            needsChoice: this.rows.filter(r => r.state === "NeedsChoice" && !this.isDecided(r)).length,
            duplicate: this.rows.filter(r => this.duplicateOf(r).length > 0).length,
            rejected: this.rows.filter(r => r.state === "Rejected").length,
            unchecked: this.rows.filter(r => r.state === "Selected" || r.state === "Checking").length
        };
    }
    // --- Queuing ---
    get busy() {
        return this.checking || this.queueing;
    }
    get canCheck() {
        return !this.busy && !!this.selectedCompany && !!this.selectedSite && this.counts.unchecked > 0;
    }
    get importBlockedReason() {
        if (!this.selectedCompany || !this.selectedSite)
            return "Select the company and site.";
        if (!this.importableRows.length)
            return "There are no files that can be imported.";
        if (this.counts.duplicate)
            return "Remove the extra files for the same controller.";
        if (this.counts.needsChoice)
            return "Choose what to do with each existing system.";
        return "";
    }
    get canImport() {
        return !this.busy && !this.counts.unchecked && !this.importBlockedReason;
    }
    rowClass(row) {
        if (row.state === "Rejected")
            return "table-danger";
        if (this.duplicateOf(row).length)
            return "table-warning";
        if (row.state === "NeedsChoice" && !this.isDecided(row))
            return "table-warning";
        return "";
    }
    queueImport() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            if (!this.canImport)
                return;
            this.queueing = true;
            this.enqueueErrors = [];
            try {
                const response = yield systemsClient.enqueueImportFromZip(new EnqueueControllerImportCommand({
                    requestModel: new EnqueueControllerImportRequestModel({
                        companyId: this.selectedCompany.value,
                        siteId: this.selectedSite.value,
                        files: this.importableRows.map(row => new EnqueueControllerImportFileModel({
                            stagingId: row.preview.stagingId,
                            overrideExistingSystem: row.state === "NeedsChoice" && !this.isDifferentLocation(row) && row.choice === "override",
                            archiveExistingSystem: row.state === "NeedsChoice" && (this.isDifferentLocation(row) ? row.archiveConfirmed : row.choice === "archive")
                        }))
                    })
                }));
                if (response.success) {
                    Notifier.onOk((_a = response.message) !== null && _a !== void 0 ? _a : "The files are queued for import.");
                    this.$emit("importQueued", response);
                    this.show = false;
                }
                else {
                    this.enqueueErrors = ((_b = response.errors) === null || _b === void 0 ? void 0 : _b.length) ? response.errors : [(_c = response.message) !== null && _c !== void 0 ? _c : "The files could not be queued."];
                }
            }
            catch (ex) {
                this.enqueueErrors = [UploadErrorHelper.getMessage(ex) || "The files could not be queued."];
            }
            finally {
                this.queueing = false;
            }
        });
    }
    // --- Display ---
    formatSize(bytes) {
        if (bytes >= 1024 * 1024)
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        if (bytes >= 1024)
            return `${Math.round(bytes / 1024)} KB`;
        return `${bytes} bytes`;
    }
    get filteredSites() {
        if (!this.selectedCompany)
            return [];
        return (this.sites || [])
            .filter(site => site.companyId === this.selectedCompany.value)
            .map(site => ({
            value: site.siteId,
            text: site.siteName || ""
        }));
    }
};
__decorate([
    Prop({ default: () => [] }),
    __metadata("design:type", typeof (_a = typeof Array !== "undefined" && Array) === "function" ? _a : Object)
], ImportSystemFromZipModal.prototype, "companies", void 0);
__decorate([
    Prop({ default: () => [] }),
    __metadata("design:type", typeof (_b = typeof Array !== "undefined" && Array) === "function" ? _b : Object)
], ImportSystemFromZipModal.prototype, "sites", void 0);
__decorate([
    Watch("selectedCompany"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], ImportSystemFromZipModal.prototype, "onCompanyChanged", null);
__decorate([
    Watch("selectedSite"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], ImportSystemFromZipModal.prototype, "onSiteChanged", null);
ImportSystemFromZipModal = __decorate([
    Component({
        components: { Multiselect },
        expose: ["showModal"],
        emits: ["importQueued"]
    })
], ImportSystemFromZipModal);
export default ImportSystemFromZipModal;
